/**
 * DlcLosses — Phase 94 (Feb 2026)
 *
 * Historique des pertes DLC (mouvements OUT/WASTE avec motif DLC).
 * Filtres : distributeur + période + source. Export CSV pour partage revendeurs.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDateTime, fmtDate, fmtNum } from "../lib/format";
import { AlertTriangle, Download, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";

const SOURCE_META = {
  WITHDRAW: { label: "Sortie unitaire",   color: "bg-[#FBEEEA] text-[#A04E2C] border-[#E8B0A0]" },
  MASS:     { label: "Retrait en masse",  color: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
  TOURNEE:  { label: "Tournée terrain",   color: "bg-[#E8F2EC] text-[#1F4E3D] border-[#A8C9B6]" },
  LEGACY:   { label: "Legacy",            color: "bg-[#F4F4F0] text-[#575A57] border-[#C8C8C0]" },
  UNKNOWN:  { label: "Autre",             color: "bg-[#F4F4F0] text-[#A3A6A3] border-[#C8C8C0]" },
};

// Helpers pour dates du picker (YYYY-MM-DD)
const isoDay = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return isoDay(d);
};

export default function DlcLosses() {
  // Phase 101fk.5 (Feb 2026) — Deep-linking depuis le rapport « Retirées ».
  // ex: /pertes-dlc?distributor_id=xxx&date_from=YYYY-MM-DD&date_to=YYYY-MM-DD
  const [searchParams] = useSearchParams();
  const [distributors, setDistributors] = useState([]);
  const [distId, setDistId] = useState(() => searchParams.get("distributor_id") || "__all");
  const [source, setSource] = useState(() => searchParams.get("source") || "__all");
  const [dateFrom, setDateFrom] = useState(() => searchParams.get("date_from") || daysAgo(30));
  const [dateTo, setDateTo] = useState(() => searchParams.get("date_to") || isoDay(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/distributors");
        setDistributors(r.data || []);
      } catch {
        toast.error("Erreur chargement distributeurs");
      }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (distId !== "__all") params.distributor_id = distId;
      if (source !== "__all") params.source = source;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/dlc-losses", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement pertes DLC");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [distId, source, dateFrom, dateTo]);

  const items = data?.items || [];
  const totalQty = data?.total_quantity || 0;
  const byDist = data?.by_distributor || {};
  const bySrc = data?.by_source || {};
  const byProduct = data?.by_product || {};

  const topProducts = useMemo(() => {
    return Object.entries(byProduct)
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);
  }, [byProduct]);

  const topDists = useMemo(() => {
    return Object.entries(byDist)
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);
  }, [byDist]);

  const handleExportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (distId !== "__all") params.append("distributor_id", distId);
      if (source !== "__all") params.append("source", source);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      const url = `/dlc-losses/export.csv?${params.toString()}`;
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `pertes-dlc-${isoDay(new Date())}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur export CSV");
    }
  };

  const handleQuickRange = (days) => {
    setDateFrom(daysAgo(days));
    setDateTo(isoDay(new Date()));
  };

  return (
    <div className="space-y-6" data-testid="dlc-losses-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#1F2F1F] flex items-center gap-2">
            <Trash2 className="w-6 h-6 text-[#A04E2C]" />
            Pertes DLC — Historique
          </h1>
          <p className="text-sm text-[#575A57] mt-1">
            Archive de toutes les barquettes retirées pour dépassement de DLC.
            Utile pour audit interne et transparence revendeurs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="dlc-losses-reload-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Rafraîchir
          </Button>
          <Button
            onClick={handleExportCsv}
            disabled={loading || items.length === 0}
            data-testid="dlc-losses-export-btn"
            className="bg-[#1F4E3D] hover:bg-[#173D30] text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <label className="text-xs font-medium text-[#575A57] mb-1 block">Distributeur</label>
              <Select value={distId} onValueChange={setDistId}>
                <SelectTrigger data-testid="dlc-losses-dist-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Tous les distributeurs</SelectItem>
                  {distributors.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#575A57] mb-1 block">Source</label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="dlc-losses-source-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Toutes sources</SelectItem>
                  <SelectItem value="WITHDRAW">Sortie unitaire</SelectItem>
                  <SelectItem value="MASS">Retrait en masse</SelectItem>
                  <SelectItem value="TOURNEE">Tournée terrain</SelectItem>
                  <SelectItem value="LEGACY">Legacy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#575A57] mb-1 block">Du</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="dlc-losses-from-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#575A57] mb-1 block">Au</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="dlc-losses-to-input"
              />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => handleQuickRange(7)} data-testid="dlc-losses-range-7d">7 jours</Button>
            <Button size="sm" variant="outline" onClick={() => handleQuickRange(30)} data-testid="dlc-losses-range-30d">30 jours</Button>
            <Button size="sm" variant="outline" onClick={() => handleQuickRange(90)} data-testid="dlc-losses-range-90d">90 jours</Button>
            <Button size="sm" variant="outline" onClick={() => handleQuickRange(365)} data-testid="dlc-losses-range-365d">1 an</Button>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card data-testid="dlc-losses-kpi-count">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">Événements</p>
            <p className="text-3xl font-semibold text-[#1F2F1F] mt-1">{data?.count || 0}</p>
          </CardContent>
        </Card>
        <Card data-testid="dlc-losses-kpi-total-qty">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">Barquettes perdues</p>
            <p className="text-3xl font-semibold text-[#A04E2C] mt-1">{fmtNum(totalQty)}</p>
          </CardContent>
        </Card>
        <Card data-testid="dlc-losses-kpi-top-dist">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">Top distributeur</p>
            <p className="text-base font-medium text-[#1F2F1F] mt-1 truncate">
              {topDists[0]?.distributor_name || "—"}
            </p>
            <p className="text-sm text-[#575A57]">
              {topDists[0] ? `${fmtNum(topDists[0].quantity)} barquettes` : "Aucune perte"}
            </p>
          </CardContent>
        </Card>
        <Card data-testid="dlc-losses-kpi-top-product">
          <CardContent className="pt-6">
            <p className="text-xs text-[#575A57] uppercase tracking-wide">Top produit</p>
            <p className="text-base font-medium text-[#1F2F1F] mt-1 truncate">
              {topProducts[0]?.product_name || "—"}
            </p>
            <p className="text-sm text-[#575A57]">
              {topProducts[0] ? `${fmtNum(topProducts[0].quantity)} barquettes` : "Aucune perte"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Répartition par source */}
      {Object.keys(bySrc).length > 0 && (
        <Card data-testid="dlc-losses-by-source">
          <CardContent className="pt-6">
            <h3 className="text-sm font-medium text-[#575A57] mb-3">Répartition par source</h3>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(bySrc).map(([src, qty]) => {
                const meta = SOURCE_META[src] || SOURCE_META.UNKNOWN;
                return (
                  <div
                    key={src}
                    className={`px-3 py-2 rounded-md border ${meta.color}`}
                    data-testid={`dlc-losses-source-${src}`}
                  >
                    <span className="font-medium">{meta.label}</span>
                    <span className="ml-2 text-xs opacity-80">{fmtNum(qty)} barquette{qty > 1 ? "s" : ""}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tableau */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-12 text-[#575A57]">
              <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
              Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-[#575A57]" data-testid="dlc-losses-empty">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Aucune perte DLC sur cette période.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="dlc-losses-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date sortie</TableHead>
                    <TableHead>Distributeur</TableHead>
                    <TableHead>Produit</TableHead>
                    <TableHead>Lot</TableHead>
                    <TableHead>DLC</TableHead>
                    <TableHead className="text-right">Quantité</TableHead>
                    <TableHead>Source</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => {
                    const meta = SOURCE_META[it.source] || SOURCE_META.UNKNOWN;
                    return (
                      <TableRow key={it.id} data-testid={`dlc-losses-row-${it.id}`}>
                        <TableCell className="text-sm">{fmtDateTime(it.created_at)}</TableCell>
                        <TableCell className="text-sm">{it.distributor_name}</TableCell>
                        <TableCell>
                          <div className="text-sm font-medium text-[#1F2F1F]">{it.product_name}</div>
                          {it.product_sku && (
                            <div className="text-xs text-[#575A57]">{it.product_sku}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm font-mono">{it.lot_number || "—"}</TableCell>
                        <TableCell className="text-sm">{it.lot_dlc ? fmtDate(it.lot_dlc) : "—"}</TableCell>
                        <TableCell className="text-right font-medium text-[#A04E2C]">
                          {fmtNum(it.quantity)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${meta.color}`}>{meta.label}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Encadré pédagogique */}
      <Card className="bg-[#F8F5EE] border-[#E8D3A8]">
        <CardContent className="pt-6 text-sm text-[#575A57]">
          <p className="font-medium text-[#8C6B2F] mb-2">Comment lire cet historique ?</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><span className="font-medium">Sortie unitaire</span> : lot retiré au comptoir via bouton « Sortir barquette » (motif DLC).</li>
            <li><span className="font-medium">Retrait en masse</span> : action groupée sur tous les lots ≤ date pivot (page Stocks).</li>
            <li><span className="font-medium">Tournée terrain</span> : perte imputée lors d&apos;une tournée de réappro (opérateur mobile).</li>
            <li><span className="font-medium">Legacy</span> : ancien endpoint `POST /lots/{'{id}'}/expire` conservé pour compatibilité.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
